import React, { useEffect, useMemo, useState } from "react";
import { getMarketRegime } from "../services/api";
import OrderFlowGateStatus from "./OrderFlowGateStatus";
import "../styles/LiveBlocks.css";

const SECTION_DEFS = [
  { key: "analysis", title: "Análisis de Indicadores", match: /Análisis de Indicadores/i },
  { key: "operativo", title: "Bloque Operativo", match: /Bloque Operativo/i },
  { key: "checklist", title: "Análisis Operativo", match: /Análisis Operativo|Checklist Principal/i },
  { key: "normales", title: "Filtros Normales", match: /Filtros Normales/i },
  { key: "ultra", title: "Filtros ULTRA EXPRESS", match: /Filtros ULTRA EXPRESS/i },
  { key: "express", title: "Filtros EXPRESS", match: /Filtros EXPRESS/i },
  { key: "ordenes", title: "Órdenes Abiertas", match: /Órdenes Abiertas|Orden Abierta/i },
];

const SECTION_ORDER = [
  "checklist",
  "analysis",
  "operativo",
  "normales",
  "express",
  "ultra",
  "ordenes",
];

const SECTION_TITLE_BY_KEY = SECTION_DEFS.reduce((acc, def) => {
  acc[def.key] = def.title;
  return acc;
}, {});

const STATUS_META = {
  ok: { icon: "✅", label: "Cumple" },
  bad: { icon: "❌", label: "No cumple" },
  warn: { icon: "⚠️", label: "Advertencia" },
  info: { icon: "ℹ️", label: "Info" },
  neutral: { icon: "", label: "" },
};

const normalizeText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const normalizeSymbol = (value) => {
  const raw = String(value || "").trim().toUpperCase().replace("-", "_").replace("/", "_");
  if (!raw) return "";
  if (raw.includes("_")) return raw;
  if (raw.endsWith("USDT")) return raw.replace("USDT", "_USDT");
  return raw;
};

// eslint-disable-next-line no-control-regex
const ANSI_ESCAPE_RE = new RegExp("\\u001b\\[[0-9;]*m", "g");
const stripAnsi = (value) => String(value || "").replace(ANSI_ESCAPE_RE, "");

const sanitizeLine = (value) => {
  let text = stripAnsi(value).replace(/\r/g, "").trim();
  if (!text) return "";
  text = text.replace(/^\d{4}-\d{2}-\d{2}[^|]*\|\s*[A-Z]+\s*\|\s*/, "");
  return text.trim();
};

const isSeparator = (text) => {
  if (!text) return true;
  if (/^[\s·]*[─-]{6,}\s*$/.test(text)) return true;
  if (/^[\s·]*[─-]+\s+[A-Za-zÁÉÍÓÚÑáéíóúÑ].+[─-]+/.test(text)) return false;
  return false;
};

const getSectionKey = (text) => {
  if (/^\s*·\s*/.test(text)) return null;
  const clean = text.trim();
  for (const def of SECTION_DEFS) {
    if (def.match.test(clean)) {
      return def.key;
    }
    const normalizedClean = normalizeText(clean);
    const normalizedTitle = normalizeText(def.title || "");
    if (normalizedTitle && normalizedClean.includes(normalizedTitle)) {
      return def.key;
    }
  }
  return null;
};

const isCycleHeader = (text) => /Inicio\s+(de\s+)?Ciclo/i.test(text);

const extractSymbol = (text) => {
  const raw = String(text || "").toUpperCase();
  const bracketMatch = raw.match(/\[([A-Z0-9]+(?:_[A-Z0-9]+)+(?:_PERP)?)\]/);
  if (bracketMatch) return normalizeSymbol(bracketMatch[1]);
  const plainMatch = raw.match(/\b([A-Z0-9]+(?:_[A-Z0-9]+)+(?:_PERP)?)\b/);
  return plainMatch ? normalizeSymbol(plainMatch[1]) : "";
};

const extractSectionLabel = (text) => {
  if (!text.includes("─")) return null;
  if (!/[A-Za-zÁÉÍÓÚÑáéíóúÑ]/.test(text)) return null;
  const label = text.replace(/[·\s]*[─-]+/g, " ").trim();
  return label || null;
};

const detectStatus = (text) => {
  if (text.includes("✅")) return "ok";
  if (text.includes("❌") || text.includes("⛔")) return "bad";
  if (text.includes("⚠️") || text.includes("⚡")) return "warn";
  if (text.includes("ℹ️") || text.includes("⚙️")) return "info";
  if (text.includes("🟡")) return "warn";
  if (text.includes("🟢")) return "ok";
  const lower = text.toLowerCase();
  if (lower.includes("bypass") || lower.includes("excepcio")) return "info";
  if (normalizeText(text).includes("intradia")) return "info";
  return "neutral";
};

const STATUS_EMOJIS = {
  ok: ["✅", "🟢"],
  bad: ["❌", "⛔"],
  warn: ["⚠️", "⚡", "🟡"],
  info: ["ℹ️", "⚙️"],
};

const stripStatusEmoji = (text, status) => {
  const emojis = STATUS_EMOJIS[status];
  if (!emojis) return text;
  let result = text;
  for (const emoji of emojis) {
    result = result.replaceAll(emoji, "");
  }
  return result.trim();
};

const getLastSyncTime = (logs) => {
  for (let i = logs.length - 1; i >= 0; i -= 1) {
    const ts = logs[i]?.timestamp;
    if (!ts) continue;
    const parsed = new Date(ts);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleTimeString();
    }
  }
  return "";
};

const isNotApproved = (item) => item?.type === "item" && (item.status === "bad" || item.status === "warn");

const reorderItems = (items) => {
  const ordered = [];
  let buffer = [];

  const flush = () => {
    if (!buffer.length) return;
    const notApproved = buffer.filter((item) => isNotApproved(item));
    const rest = buffer.filter((item) => !isNotApproved(item));
    ordered.push(...notApproved, ...rest);
    buffer = [];
  };

  items.forEach((item) => {
    if (item.type === "section") {
      flush();
      ordered.push(item);
      return;
    }
    buffer.push(item);
  });

  flush();
  return ordered;
};

const isOrderBatchStart = (block) => {
  const headerText = normalizeText(block?.header || block?.title || "");
  if (headerText.includes("orden abierta 1")) return true;
  const items = block?.items || [];
  return items.some((item) => {
    const text = normalizeText(item?.text || "");
    return /#\s*1\/\d+/.test(text);
  });
};

const isNoOrdersBlock = (block) => {
  const items = block?.items || [];
  if (!items.length) return true;
  return items.every((item) => {
    if (item?.type !== "item") return true;
    const text = normalizeText(item?.text || "");
    return text.includes("no hay ordenes abiertas");
  });
};

const parseDollars = (text) => {
  const matches = String(text || "").match(/\$([0-9]+(?:\.[0-9]+)?)/g);
  if (!matches) return [];
  return matches
    .map((value) => parseFloat(value.replace("$", "")))
    .filter((num) => Number.isFinite(num));
};

const parseLastNumber = (text) => {
  const matches = String(text || "").match(/([0-9]+(?:\.[0-9]+)?)/g);
  if (!matches || !matches.length) return null;
  const num = parseFloat(matches[matches.length - 1]);
  return Number.isFinite(num) ? num : null;
};

const appendOrderValues = (items) => {
  const output = [];
  let currentPrice = null;
  let inOrder = false;

  items.forEach((item) => {
    const text = item?.text || "";
    const normalized = normalizeText(text);
    const isOrderHeader =
      (item?.type === "section" && normalized.includes("orden abierta")) ||
      (item?.type === "item" && normalized.includes("orden abierta"));

    if (isOrderHeader) {
      inOrder = true;
      currentPrice = null;
      output.push(item);
      return;
    }

    if (item?.type !== "item" || !inOrder) {
      output.push(item);
      return;
    }

    if (normalized.includes("precio / pnl")) {
      const dollars = parseDollars(text);
      if (dollars.length) {
        currentPrice = dollars[dollars.length - 1];
      }
      output.push(item);
      return;
    }

    if (normalized.includes("tp / sl / size")) {
      const size = parseLastNumber(text);
      output.push(item);
      if (Number.isFinite(currentPrice) && Number.isFinite(size)) {
        const value = currentPrice * size;
        output.push({
          type: "item",
          status: "info",
          text: `💵 Valor orden (USD): $${value.toFixed(2)}`,
        });
      }
      return;
    }

    output.push(item);
  });

  return output;
};

const buildOrdersBlockFromTail = (blocks) => {
  const orderBlocks = blocks.filter((block) => block.key === "ordenes");
  if (!orderBlocks.length) return null;

  const collected = [];
  for (let i = orderBlocks.length - 1; i >= 0; i -= 1) {
    const block = orderBlocks[i];
    collected.push(block);
    if (isNoOrdersBlock(block) || isOrderBatchStart(block)) {
      break;
    }
  }

  const normalizedBlocks = collected
    .reverse()
    .map((block) => ({ ...block, items: reorderItems(block.items || []) }));

  if (normalizedBlocks.length === 1 && isNoOrdersBlock(normalizedBlocks[0])) {
    return normalizedBlocks[0];
  }

  const mergedItems = [];
  normalizedBlocks.forEach((block) => {
    const headerText = String(block.header || block.title || "");
    const headerNormalized = normalizeText(headerText);
    if (headerText && !headerNormalized.includes("ordenes abiertas")) {
      mergedItems.push({ type: "section", text: headerText });
    }
    mergedItems.push(...(block.items || []));
  });

  const lastBlock = normalizedBlocks[normalizedBlocks.length - 1];
  return {
    key: "ordenes",
    title: SECTION_TITLE_BY_KEY.ordenes || "Órdenes Abiertas",
    header: SECTION_TITLE_BY_KEY.ordenes || "Órdenes Abiertas",
    items: appendOrderValues(mergedItems),
    ts: lastBlock?.ts || "",
  };
};

const buildBlocks = (logs, symbol = "") => {
  const selectedSymbol = normalizeSymbol(symbol);
  const blocks = [];
  let current = null;
  let activeSymbol = "";

  logs.forEach((entry) => {
    const rawText = stripAnsi(entry?.message || "").replace(/\r/g, "");
    const parts = rawText.split("\n");
    parts.forEach((part) => {
      const raw = sanitizeLine(part);
      if (!raw) return;
      const lineSymbol = extractSymbol(raw);
      if (isCycleHeader(raw)) {
        activeSymbol = lineSymbol || activeSymbol;
        if (!current) {
          current = null;
        }
        return;
      }
      const key = getSectionKey(raw);
      if (key) {
        const blockSymbol = lineSymbol || activeSymbol || selectedSymbol;
        if (selectedSymbol && blockSymbol && blockSymbol !== selectedSymbol) {
          current = null;
          return;
        }
        if (selectedSymbol && !blockSymbol) {
          current = null;
          return;
        }
        current = {
          key,
          title: SECTION_DEFS.find((def) => def.key === key)?.title || raw,
          header: raw,
          symbol: blockSymbol,
          items: [],
          ts: entry?.timestamp || "",
        };
        blocks.push(current);
        return;
      }

      if (!current) return;
      if (isSeparator(raw)) return;
      const sectionLabel = extractSectionLabel(raw);
      if (sectionLabel) {
        current.items.push({ type: "section", text: sectionLabel });
        return;
      }

      const cleaned = raw.replace(/^·\s*/, "").trim();
      if (!cleaned) return;
      current.items.push({
        type: "item",
        text: cleaned,
        status: detectStatus(cleaned),
      });
    });
  });

  const latest = {};
  blocks.forEach((block) => {
    if (block.key === "ordenes") {
      return;
    }
    const normalized = {
      ...block,
      items: reorderItems(block.items || []),
    };
    latest[block.key] = normalized;
  });

  const ordersBlock = buildOrdersBlockFromTail(blocks);
  if (ordersBlock) {
    latest.ordenes = ordersBlock;
  }

  let checklistHeadline = null;
  const checklist = latest.checklist;
  if (checklist?.items?.length) {
    const checklistItems = checklist.items.filter((item) => item.type === "item");
    if (checklistItems.length === 1) {
      checklistHeadline = checklistItems[0];
    }
  }

  return { blocks: latest, headline: checklistHeadline };
};

const MetaBadges = ({ header }) => {
  const parts = [];
  const regex = /\[([^\]]+)\]/g;
  let match;
  while ((match = regex.exec(header)) !== null) {
    parts.push(match[1]);
  }
  if (!parts.length) return null;
  return (
    <div className="live-block-meta">
      {parts.map((part, idx) => (
        <span className="live-block-badge" key={`${part}-${idx}`}>
          {part}
        </span>
      ))}
    </div>
  );
};

const ChecklistSummary = ({ items }) => {
  const checklistItems = items.filter((item) => item.type === "item");
  const total = checklistItems.length;
  const passed = checklistItems.filter((item) => item.status === "ok").length;
  const failed = checklistItems.filter((item) => item.status === "bad").length;
  const warnings = checklistItems.filter((item) => item.status === "warn").length;

  if (!total) return null;

  const score = total > 0 ? Math.round((passed / total) * 100) : 0;
  const scoreClass = score >= 80 ? "high" : score >= 50 ? "mid" : "low";

  return (
    <div className={`checklist-summary score-${scoreClass}`}>
      <div className="checklist-summary-stats">
        <span className="checklist-stat ok">✅ {passed}</span>
        <span className="checklist-stat bad">❌ {failed}</span>
        {warnings > 0 && <span className="checklist-stat warn">⚠️ {warnings}</span>}
        <span className="checklist-stat total">{total} condiciones</span>
      </div>
      <div className="checklist-summary-bar">
        <div
          className={`checklist-summary-fill score-${scoreClass}`}
          style={{ width: `${score}%` }}
        />
      </div>
      <span className={`checklist-summary-score score-${scoreClass}`}>
        {score}% cumplidas
      </span>
    </div>
  );
};

const MethodSummaryCard = ({ method }) => {
  const conditions = Array.isArray(method.condiciones) ? method.condiciones : [];
  const ok = conditions.filter((c) => c.ok).length;
  const total = conditions.length;
  const pct = total ? Math.round((ok / total) * 100) : 0;
  const scoreClass = pct >= 80 ? "high" : pct >= 50 ? "mid" : "low";
  const status = total && ok === total ? "ok" : (ok > 0 ? "warn" : "fail");
  const statusIcon = status === "ok" ? "✅" : status === "warn" ? "🟡" : "❌";

  return (
    <section className={`live-block live-block-method status-${status}`.trim()}>
      <header className="live-block-header">
        <div>
          <h3>{method.label}</h3>
          <div className="live-block-meta">
            <span className="live-block-badge">{ok}/{total} reglas</span>
            <span className={`method-summary-card-score score-${scoreClass}`}>{pct}% cumplidas</span>
          </div>
        </div>
        <span className="live-block-method-icon" title={status === "ok" ? "OK" : status === "warn" ? "Parcial" : "Falla"}>
          {statusIcon}
        </span>
      </header>
      <div className="live-block-method-bar-score">
        <div className="live-block-method-bar">
          <div
            className={`live-block-method-fill score-${scoreClass}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className={`live-block-method-pct score-${scoreClass}`}>{pct}%</span>
      </div>
      <div className="live-block-body">
        {conditions.length ? (
          <ul className="live-block-list">
            {conditions.map((c, ci) => (
              <li
                key={`${method.key}-${ci}`}
                className={`live-block-item ${c.ok ? "status-ok" : "status-warn"}`}
              >
                <ItemIndicator status={c.ok ? "ok" : "warn"} />
                <span className="live-block-text">{c.nombre}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="live-block-body-empty">Sin condiciones registradas.</div>
        )}
      </div>
      <MethodPrediction
        methodKey={method.key}
        prediccion={method.prediccion}
        gateGlobal={method.gate_global}
      />
    </section>
  );
};

const MethodPrediction = ({ methodKey, prediccion, gateGlobal }) => {
  const pred = prediccion && typeof prediccion === "object" ? prediccion : null;
  if (!pred) return null;

  // Veredicto real del gate global 5M (el que aplica el motor). Viene del
  // backend con la misma función que bloquea la compra: sin esto el panel solo
  // mostraba la predicción por método (ya ajustada por alineación) y daba
  // "✅ permite la compra" mientras el bot no entraba.
  const gate =
    gateGlobal && typeof gateGlobal === "object" && gateGlobal.bloquea !== null && gateGlobal.bloquea !== undefined
      ? gateGlobal
      : null;
  const hasHist = typeof pred.win_rate === "number" || typeof pred.rr === "number";
  const hasNews = pred.news_riesgo_label || typeof pred.news_score === "number";
  if (!hasHist && !hasNews && !gate) return null;

  const neto = typeof pred.neto === "number" ? pred.neto : null;
  const netoCls =
    neto === null ? "muted" : neto >= 0 ? "positive" : "negative";
  const hasData = (pred.metodo_n || 0) > 0 || (pred.samples || 0) > 0;

  const Chip = ({ label, value, cls = "" }) => (
    <span className={`method-pred-chip ${cls}`.trim()}>
      <span className="method-pred-chip-label">{label}</span>
      <span className="method-pred-chip-value">{value}</span>
    </span>
  );

  return (
    <div className="method-pred-section">
      {gate ? (
        <>
          <div className="method-pred-separator" />
          <div className="method-pred-title">🚦 Gate global 5M (decide la compra)</div>
          <div
            className={`method-pred-status ${
              gate.permite_compra ? "allowed" : "blocked"
            }`}
          >
            {gate.permite_compra
              ? gate.override_metodo
                ? "🟢 Permitido por override del método"
                : "✅ Permite la compra"
              : "⛔ Bloquea la compra (gate global)"}
          </div>
          <div className="method-pred-grid">
            <Chip
              label="WR 5M real"
              value={`${Number(gate.wr_5m || 0).toFixed(0)}%`}
              cls={gate.wr_5m >= gate.min_wr ? "positive" : "negative"}
            />
            <Chip label="Mín." value={`${gate.min_wr}%`} cls="muted" />
            <Chip label="Muestras" value={String(gate.samples ?? 0)} />
            <Chip
              label="Estabilidad"
              value={gate.estabilidad_ok ? "estable" : "inestable"}
              cls={gate.estabilidad_ok ? "positive" : "negative"}
            />
          </div>
          {gate.detalle ? (
            <div className="method-pred-motivo">Motivo: {gate.detalle}</div>
          ) : null}
          {gate.override_metodo ? (
            <div className="method-pred-motivo">
              Override: {gate.override_metodo}
            </div>
          ) : null}
        </>
      ) : null}
      <div className="method-pred-separator" />
      <div className="method-pred-title">📈 Predicción del método</div>
      {hasData ? (
        <div
          className={`method-pred-status ${pred.bloquea ? "blocked" : "allowed"}`}
        >
          {pred.bloquea
            ? "⛔ Bloquea la compra"
            : "✅ Permite la compra"}
        </div>
      ) : null}
      {typeof pred.alineacion === "number" ? (
        <div className="method-pred-grid">
          <Chip
            label="Alineación"
            value={`${(pred.alineacion * 100).toFixed(0)}%`}
            cls={pred.alineacion >= 0.6 ? "positive" : pred.alineacion < 0.4 ? "negative" : "muted"}
          />
        </div>
      ) : null}
      {pred.metodo_n > 0 ? (
        <div className="method-pred-grid">
          <span className="method-pred-key">🎯 Este método en el par</span>
          <Chip label="Ventas" value={String(pred.metodo_n)} />
          <Chip
            label="WR"
            value={`${pred.metodo_wr.toFixed(0)}%`}
            cls={pred.metodo_wr >= 50 ? "positive" : "negative"}
          />
          <Chip
            label="Avg P&L"
            value={`${pred.metodo_avg >= 0 ? "+" : ""}${pred.metodo_avg.toFixed(2)}%`}
            cls={pred.metodo_avg >= 0 ? "positive" : "negative"}
          />
        </div>
      ) : null}
      <div className="method-pred-grid">
        {typeof pred.win_rate === "number" ? (
          <Chip
            label="WR pred."
            value={`${pred.win_rate.toFixed(0)}%`}
            cls={pred.win_rate >= 50 ? "positive" : "negative"}
          />
        ) : null}
        {typeof pred.rr === "number" && pred.rr > 0 ? (
          <Chip label="RR" value={pred.rr.toFixed(2)} />
        ) : null}
        {typeof pred.upside_p50 === "number" ? (
          <Chip label="Upside p50" value={`${pred.upside_p50 >= 0 ? "+" : ""}${pred.upside_p50.toFixed(3)}%`} />
        ) : null}
        {typeof pred.downside_p50 === "number" ? (
          <Chip label="Downside p50" value={`${pred.downside_p50 >= 0 ? "+" : ""}${pred.downside_p50.toFixed(3)}%`} />
        ) : null}
        {neto !== null ? (
          <Chip
            label="Neto pred."
            value={`${neto >= 0 ? "+" : ""}${neto.toFixed(3)}%`}
            cls={netoCls}
          />
        ) : null}
        {typeof pred.samples === "number" && pred.samples > 0 ? (
          <Chip label="Muestras" value={String(pred.samples)} />
        ) : null}
        {pred.news_riesgo_label ? (
          <Chip
            label="Noticias"
            value={pred.news_riesgo_label}
            cls={pred.news_riesgo_label === "ALTO" ? "negative" : pred.news_riesgo_label === "BAJO" ? "positive" : "muted"}
          />
        ) : null}
        {typeof pred.news_score === "number" ? (
          <Chip
            label="Score noticias"
            value={pred.news_score.toFixed(1)}
            cls={pred.news_score > 0 ? "positive" : pred.news_score < 0 ? "negative" : "muted"}
          />
        ) : null}
        {typeof pred.news_negativas === "number" && pred.news_negativas > 0 ? (
          <Chip label="Alertas neg." value={String(pred.news_negativas)} cls="negative" />
        ) : null}
      </div>
    </div>
  );
};

const ItemIndicator = ({ status }) => {
  const hasStatus = status && status !== "neutral";
  if (!hasStatus) return null;
  const meta = STATUS_META[status] || STATUS_META.neutral;
  return (
    <span className="live-block-item-indicator" title={meta.label}>
      {meta.icon}
    </span>
  );
};

const BlockCard = ({ block, timeLabel, title, blockKey, emptyMessage, maxItems = 24, symbol = "" }) => {
  const rawItems = block?.items || [];
  const items = Number.isFinite(maxItems) ? rawItems.slice(0, maxItems) : rawItems;
  const resolvedKey = block?.key || blockKey || "empty";
  const resolvedTitle = block?.title || title || "Bloque";
  const isEmpty = items.length === 0;
  const isChecklist = resolvedKey === "checklist";

  return (
    <section className={`live-block live-block-${resolvedKey} ${isEmpty ? "is-empty" : ""}`.trim()}>
      <header className="live-block-header">
        <div className="live-block-header-title">
          <h3>{resolvedTitle}</h3>
          {symbol ? <span className="live-block-symbol">{symbol}</span> : null}
        </div>
        {timeLabel ? <span className="live-block-time">{timeLabel}</span> : null}
      </header>
      {isChecklist && !isEmpty && <ChecklistSummary items={items} />}
      <div className="live-block-body">
        {items.length ? (
          <ul className="live-block-list">
            {items.map((item, idx) => {
              if (item.type === "section") {
                return (
                  <li className="live-block-section" key={`section-${idx}`}>
                    {item.text}
                  </li>
                );
              }
              const status = item.status || "neutral";
              const cleanText = stripStatusEmoji(item.text, status);
              return (
                <li
                  key={`item-${idx}`}
                  className={`live-block-item status-${status}`}
                >
                  <ItemIndicator status={status} />
                  <span className="live-block-text">{cleanText}</span>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="live-block-body-empty">{emptyMessage || "Esperando datos..."}</div>
        )}
      </div>
    </section>
  );
};

const LIVE_BLOCKS_MAX_LOGS = 200;

const LiveBlocks = ({ logs = [], symbol = "" }) => {
  const currentSymbol = useMemo(() => normalizeSymbol(symbol), [symbol]);
  const recentLogs = useMemo(
    () => (logs.length > LIVE_BLOCKS_MAX_LOGS ? logs.slice(-LIVE_BLOCKS_MAX_LOGS) : logs),
    [logs],
  );
  const { blocks, headline } = useMemo(() => buildBlocks(recentLogs, currentSymbol), [recentLogs, currentSymbol]);
  const lastSyncTime = useMemo(() => getLastSyncTime(logs), [logs]);
  const hasData = Boolean(headline) || SECTION_ORDER.some((key) => blocks[key]?.items?.length);
  const [stickyBlocks, setStickyBlocks] = useState({});
  const [stickyHeadline, setStickyHeadline] = useState(null);
  const [stickyLastSync, setStickyLastSync] = useState("");
  const [regime, setRegime] = useState(null);
  const [methodsOpen, setMethodsOpen] = useState(false);

  useEffect(() => {
    setStickyBlocks({});
    setStickyHeadline(null);
    setStickyLastSync("");
    setRegime(null);
    setMethodsOpen(false);
  }, [currentSymbol]);

  useEffect(() => {
    if (!currentSymbol) return;
    let active = true;
    const fetchRegime = async () => {
      // No molestar a la API ni re-renderizar cuando la pestaña está en segundo plano.
      if (document.hidden) return;
      const res = await getMarketRegime(currentSymbol);
      if (active && res.ok) setRegime(res.data);
    };
    fetchRegime();
    const interval = setInterval(fetchRegime, 30000);
    return () => { active = false; clearInterval(interval); };
  }, [currentSymbol]);

  useEffect(() => {
    if (lastSyncTime) {
      setStickyLastSync(lastSyncTime);
    }
    if (headline) {
      setStickyHeadline(headline);
    }
    const keys = Object.keys(blocks || {});
    if (!keys.length) return;
    setStickyBlocks((prev) => {
      const next = { ...prev };
      keys.forEach((key) => {
        const block = blocks[key];
        if (!block) return;
        if (block.items?.length) {
          next[key] = block;
          return;
        }
        if (block.header || block.ts) {
          const existing = next[key];
          if (existing) {
            next[key] = {
              ...existing,
              header: block.header || existing.header,
              title: block.title || existing.title,
              ts: block.ts || existing.ts,
              key: block.key || existing.key,
            };
          } else {
            next[key] = block;
          }
        }
      });
      return next;
    });
  }, [blocks, headline, lastSyncTime]);

  const effectiveBlocks = Object.keys(stickyBlocks).length ? stickyBlocks : blocks;
  const effectiveHeadline = stickyHeadline || headline;
  const hasStickyData =
    Boolean(effectiveHeadline) ||
    (regime?.methods && regime.methods.length > 0) ||
    SECTION_ORDER.some((key) => effectiveBlocks[key]?.items?.length);

  return (
    <div className="live-blocks">
      {currentSymbol ? (
        <div className="live-blocks-symbol-filter">
          Mostrando análisis en vivo de <strong>{currentSymbol}</strong>
          {regime && regime.regimen ? (
            <span className={`regime-badge regime-${regime.regimen}`}>
              {regime.regimen === "muy_agresivo" ? "🚀 MUY AGRESIVO" :
               regime.regimen === "agresivo" ? "🔥 AGRESIVO" :
               regime.regimen === "moderado" ? "⚖️ MODERADO" :
               regime.regimen === "cauteloso" ? "🛡️ CAUTELOSO" : regime.regimen}
              <span className="regime-factor">×{regime.factor}</span>
            </span>
          ) : null}
          {regime?.orders ? (
            <span className={`orders-badge ${regime.orders.limit_raw === 0 || !regime.orders.limit_raw ? "orders-unlimited" : ""}`}>
              📦 {regime.orders.open || 0}/{regime.orders.limit_raw === 0 || !regime.orders.limit_raw ? "∞" : (regime.orders.limit_compose ?? regime.orders.limit_raw)} órdenes
            </span>
          ) : null}
          {regime?.methods?.some((m) => m.active) ? (
            <span className="combination-badge" title="Métodos activos de la combinación">
              <span className="combination-name">Combinación: X7</span>
              {regime.methods
                .filter((m) => m.active)
                .slice(0, 6)
                .map((m) => (
                  <span key={m.key || m.label} className="combination-method">
                    {m.label}
                  </span>
                ))}
            </span>
          ) : null}
        </div>
      ) : null}
      {currentSymbol ? (
        <div className="live-blocks-symbol-filter live-blocks-orderflow">
          <OrderFlowGateStatus symbol={currentSymbol} />
        </div>
      ) : null}
      {regime?.methods?.length ? (
        <div className={`methods-panel ${methodsOpen ? "open" : ""}`}>
          <button
            type="button"
            className="methods-panel-header"
            onClick={() => setMethodsOpen((open) => !open)}
            aria-expanded={methodsOpen}
          >
            <span>Rutas de entrada — {currentSymbol}</span>
            <span className="methods-count">
              {regime.methods.filter((m) => m.active).length}/{regime.methods.length} listas
              <span className={`methods-toggle ${methodsOpen ? "open" : ""}`}>▾</span>
            </span>
          </button>
          {methodsOpen ? (
            <div className="methods-list">
              {[...regime.methods]
                .sort((a, b) => {
                  if (a.selected && !b.selected) return -1;
                  if (!a.selected && b.selected) return 1;
                  if (a.active && !b.active) return -1;
                  if (!a.active && b.active) return 1;
                  return a.label.localeCompare(b.label);
                })
                .map((method) => (
                  <span key={method.key || method.label} className={`method-dot ${method.active ? "method-dot-active" : "method-dot-inactive"}`} title={method.label}>
                    {method.label}
                  </span>
                ))}
            </div>
          ) : null}
        </div>
      ) : null}
      {(effectiveBlocks.checklist || regime?.methods) ? (
        <BlockCard
          key="checklist"
          block={effectiveBlocks.checklist}
          blockKey="checklist"
          title="Análisis Operativo"
          emptyMessage="Sin condiciones evaluadas en este ciclo."
          maxItems={null}
          symbol={currentSymbol}
          timeLabel={
            effectiveBlocks.checklist && lastSyncTime
              ? `Última sincronización: ${lastSyncTime}`
              : ""
          }
        />
      ) : null}
      {regime?.methods?.some((m) => m.active) ? (
        [...regime.methods]
          .filter((m) => m.active)
          .sort((a, b) => {
            if (a.selected && !b.selected) return -1;
            if (!a.selected && b.selected) return 1;
            if (a.conditions_ok && !b.conditions_ok) return -1;
            if (!a.conditions_ok && b.conditions_ok) return 1;
            return a.label.localeCompare(b.label);
          })
          .map((method) => <MethodSummaryCard key={method.key} method={method} />)
      ) : null}
      {!hasData && !hasStickyData && (
        <div className="live-blocks-empty">
          Esperando información del WebSocket...
        </div>
      )}
    </div>
  );
};

export default React.memo(LiveBlocks);
